import type { JSONContent } from "@tiptap/core"
import { acceptJsonHeaders, csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"
import type { ShotFrameSource } from "@/lib/workspace-api"

export type LocationWritePayload = {
  name?: string
  setting_type?: string | null
  profile_text?: string | null
  profile_doc?: JSONContent | null
  facing_prompt_texts?: Partial<Record<"north" | "east" | "west" | "south", string>>
  task_id?: number | null
}

export type LocationReferenceVersionRow = {
  id: number
  variant_id: number | null
  facing: LocationStorableFacing
  preview_url: string | null
  download_url?: string | null
  origin: string
  created_at: string
  active: boolean
}

export type LocationPromptPreview = {
  system: string
  user: string
  variant_id: number | null
  facing?: string
}

export const LOCATION_UI_VIEWS = ["three_quarter", "topview"] as const
export type LocationUiView = (typeof LOCATION_UI_VIEWS)[number]

/** Стороны света для shot binding / compass UI (не Locations photo panel). */
export const LOCATION_CARDINAL_FACINGS = ["north", "east", "west", "south"] as const
export type LocationCardinalFacing = (typeof LOCATION_CARDINAL_FACINGS)[number]

/** Порядок кардиналов на доске локации: С Ю З В. */
export const LOCATION_BOARD_CARDINALS = ["north", "south", "west", "east"] as const
export type LocationBoardCardinal = (typeof LOCATION_BOARD_CARDINALS)[number]

export const LOCATION_FACINGS = [...LOCATION_UI_VIEWS, ...LOCATION_CARDINAL_FACINGS] as const
export type LocationFacing = (typeof LOCATION_FACINGS)[number]

/** Room-viewer equirect panorama (not a shot location_facing). */
export const LOCATION_PANORAMA_FACING = "panorama" as const
export type LocationPanoramaFacing = typeof LOCATION_PANORAMA_FACING

/** Upper band stacked on a cardinal wall. Not a shot location_facing. */
export const LOCATION_ABOVE_FACINGS = ["north_above", "east_above", "south_above", "west_above"] as const
export type LocationAboveFacing = (typeof LOCATION_ABOVE_FACINGS)[number]

export type LocationStorableFacing = LocationFacing | LocationPanoramaFacing | LocationAboveFacing

export function aboveFacingFor(facing: LocationCardinalFacing): LocationAboveFacing {
  return `${facing}_above`
}

/** Основной фото-вид локации (3/4) — primary reference_sheet / Locations UI. */
export const PRIMARY_LOCATION_FACING: LocationFacing = "three_quarter"

/** Дефолт ракурса шота — тот же primary, чтобы strict-генерация сториборда видела кадр без cardinal north. */
export const PRIMARY_SHOT_LOCATION_FACING: LocationFacing = PRIMARY_LOCATION_FACING

export type LocationFacingPrereqReason =
  | "needThreeQuarter"
  | "needNorth"
  | "needEastWest"
  | "needGroundLevel"
  | "needPanoramaBase"

function facingHasUrl(
  urlsByFacing: Partial<Record<LocationStorableFacing, string | null>> | undefined,
  facing: LocationStorableFacing,
  primaryFallbackUrl?: string | null,
): boolean {
  // When facing key is present, honor explicit null (cleared) — do not fall back.
  if (urlsByFacing && Object.prototype.hasOwnProperty.call(urlsByFacing, facing)) {
    const u = urlsByFacing[facing]
    return typeof u === "string" && u.length > 0
  }
  if (facing === PRIMARY_LOCATION_FACING && typeof primaryFallbackUrl === "string" && primaryFallbackUrl.length > 0) {
    return true
  }
  return false
}

/** Panorama i2i anchor: 3/4 (incl. reference sheet), else north. Mirrors backend panorama_anchor_facing. */
export function panoramaGenerationBlockReason(
  urlsByFacing?: Partial<Record<LocationStorableFacing, string | null>>,
  primaryFallbackUrl?: string | null,
  locationUrlsByFacing?: Partial<Record<LocationStorableFacing, string | null>>,
  locationPrimaryFallbackUrl?: string | null,
): LocationFacingPrereqReason | null {
  const hasNorth =
    facingHasUrl(urlsByFacing, "north", primaryFallbackUrl) ||
    facingHasUrl(locationUrlsByFacing, "north", locationPrimaryFallbackUrl)
  const hasThreeQuarter =
    facingHasUrl(urlsByFacing, "three_quarter", primaryFallbackUrl) ||
    facingHasUrl(locationUrlsByFacing, "three_quarter", locationPrimaryFallbackUrl)

  if (!hasNorth && !hasThreeQuarter) return "needPanoramaBase"
  return null
}

/** Why generate is blocked for this facing, or null if allowed (client-side mirror of apply! gates). */
export function facingGenerationBlockReason(
  facing: LocationStorableFacing,
  urlsByFacing?: Partial<Record<LocationStorableFacing, string | null>>,
  primaryFallbackUrl?: string | null,
): LocationFacingPrereqReason | null {
  if (facing === LOCATION_PANORAMA_FACING) {
    return panoramaGenerationBlockReason(urlsByFacing, primaryFallbackUrl)
  }
  if (facing === "north") {
    if (!facingHasUrl(urlsByFacing, "three_quarter", primaryFallbackUrl)) return "needThreeQuarter"
    return null
  }
  if (facing === "topview") {
    const hasGround =
      facingHasUrl(urlsByFacing, "three_quarter", primaryFallbackUrl) ||
      facingHasUrl(urlsByFacing, "north", primaryFallbackUrl)
    if (!hasGround) return "needGroundLevel"
    return null
  }
  if (facing === "east") {
    if (!facingHasUrl(urlsByFacing, "north", primaryFallbackUrl)) return "needNorth"
    return null
  }
  if (facing === "west") {
    if (!facingHasUrl(urlsByFacing, "north", primaryFallbackUrl)) return "needNorth"
    if (!facingHasUrl(urlsByFacing, "three_quarter", primaryFallbackUrl)) return "needThreeQuarter"
    return null
  }
  if (facing === "south") {
    if (!facingHasUrl(urlsByFacing, "north", primaryFallbackUrl)) return "needNorth"
    if (
      !facingHasUrl(urlsByFacing, "east", primaryFallbackUrl) ||
      !facingHasUrl(urlsByFacing, "west", primaryFallbackUrl)
    ) {
      return "needEastWest"
    }
    return null
  }
  return null
}

export async function createLocation(projectId: number, payload: LocationWritePayload & { name: string }) {
  const res = await fetch(`/projects/${projectId}/workspace/locations`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ location: payload }),
  })
  return handleJson<{ ok: boolean; location_id: number; message?: string }>(res)
}

export type LocationAboveNudgePx = Partial<Record<LocationCardinalFacing, number>>

export async function updateLocationAboveNudge(
  projectId: number,
  locationId: number,
  facing: LocationCardinalFacing,
  px: number,
  variantId?: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/update_above_nudge`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ facing, px, variant_id: variantId ?? null }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function updateLocation(projectId: number, locationId: number, payload: Partial<LocationWritePayload>) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ location: payload }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function deleteLocation(projectId: number, locationId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function previewLocationReferencePrompt(
  projectId: number,
  locationId: number,
  variantId: number | null,
  facing: LocationStorableFacing = PRIMARY_LOCATION_FACING,
): Promise<LocationPromptPreview> {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/preview_reference_prompt`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ variant_id: variantId, facing }),
  })
  return handleJson<LocationPromptPreview>(res)
}

export async function generateLocationReference(
  projectId: number,
  locationId: number,
  variantId: number | null,
  opts?: {
    correctionPrompt?: string
    sourceVersionId?: number
    facing?: LocationStorableFacing
    modelKey?: string
    aspectRatio?: string
    imageSize?: string
    userPrompt?: string
    muteBuiltin?: boolean
    excludeFacings?: string[]
    excludeStyleSignedIds?: string[]
    extraReferenceUrls?: string[]
  },
) {
  const body: Record<string, unknown> = { variant_id: variantId, facing: opts?.facing ?? PRIMARY_LOCATION_FACING }
  if (opts?.correctionPrompt) body.correction_prompt = opts.correctionPrompt
  if (opts?.sourceVersionId) body.source_version_id = opts.sourceVersionId
  if (opts?.modelKey) body.model_key = opts.modelKey
  if (opts?.aspectRatio) body.aspect_ratio = opts.aspectRatio
  if (opts?.imageSize) body.image_size = opts.imageSize
  if (opts?.userPrompt) body.user_prompt = opts.userPrompt
  if (opts?.muteBuiltin) body.mute_builtin = true
  if (opts?.excludeFacings?.length) body.exclude_facings = opts.excludeFacings
  if (opts?.excludeStyleSignedIds?.length) body.exclude_style_signed_ids = opts.excludeStyleSignedIds
  if (opts?.extraReferenceUrls?.length) body.extra_reference_urls = opts.extraReferenceUrls
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/generate_reference`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function registerLocationSeedanceAsset(projectId: number, locationId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/register_seedance_asset`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function addLocationReferenceInput(
  projectId: number,
  locationId: number,
  file: File,
  variantId?: number | null,
  meta?: { sourceReferenceKey?: string },
) {
  const form = new FormData()
  form.append("file", file)
  if (variantId) form.append("variant_id", String(variantId))
  if (meta?.sourceReferenceKey) form.append("source_reference_key", meta.sourceReferenceKey)
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/add_reference_input`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

/** Референс из Библиотеки: сервер берёт существующий блоб, файл не копируется. */
export async function addLocationReferenceInputFromSource(
  projectId: number,
  locationId: number,
  source: ShotFrameSource,
  variantId?: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/add_reference_input`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source, variant_id: variantId ?? undefined }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function removeLocationReferenceInput(
  projectId: number,
  locationId: number,
  blobSignedId: string,
  variantId?: number | null,
) {
  const body: Record<string, unknown> = { blob_signed_id: blobSignedId }
  if (variantId) body.variant_id = variantId
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/remove_reference_input`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export type LocationViewpointCameraMeta = {
  pos: [number, number, number]
  yaw: number
  pitch: number
  fov: number
  shape: "cube" | "cylinder" | "sphere"
}

/** 2D-эскиз без позы комнаты: ракурс в полоске есть, 3D restore прыгает в центр. */
export const SKETCH_VIEWPOINT_CAMERA_META: LocationViewpointCameraMeta = {
  pos: [0, 4.5, 0],
  yaw: 0,
  pitch: 0,
  fov: 60,
  shape: "cube",
}

export type LocationViewpointRow = {
  id: number
  name?: string | null
  signed_id: string
  url: string
  thumb_url?: string | null
  download_url?: string | null
  camera_meta: LocationViewpointCameraMeta
}

/** Original blob for Sketch/lightbox; `url` is display ≤800. */
export function locationViewpointDownloadUrl(vp: Pick<LocationViewpointRow, "url" | "download_url">): string | null {
  const download = vp.download_url?.trim()
  if (download) return download
  return vp.url?.trim() || null
}

export async function addLocationViewpoint(
  projectId: number,
  locationId: number,
  file: Blob,
  cameraMeta: LocationViewpointCameraMeta,
  variantId?: number | null,
) {
  const form = new FormData()
  form.append("file", file, "viewpoint.png")
  form.append("camera_meta", JSON.stringify(cameraMeta))
  if (variantId) form.append("variant_id", String(variantId))
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/add_viewpoint`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; message?: string; viewpoint_id?: number }>(res)
}

export async function removeLocationViewpoint(projectId: number, locationId: number, viewpointId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/remove_viewpoint`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ viewpoint_id: viewpointId }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function updateLocationViewpoint(
  projectId: number,
  locationId: number,
  viewpointId: number,
  payload: { name: string },
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/update_viewpoint`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ viewpoint_id: viewpointId, name: payload.name }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function createVariant(
  projectId: number,
  locationId: number,
  payload: {
    name?: string | null
    time_of_day?: string | null
    weather?: string | null
    notes?: string | null
    image_fidelity?: number | null
  },
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/variants`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ variant: payload }),
  })
  return handleJson<{ ok: boolean; variant_id: number; message?: string }>(res)
}

export async function updateVariant(
  projectId: number,
  locationId: number,
  variantId: number,
  payload: {
    name?: string | null
    time_of_day?: string | null
    weather?: string | null
    notes?: string | null
    image_fidelity?: number | null
  },
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/variants/${variantId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ variant: payload }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function deleteVariant(projectId: number, locationId: number, variantId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/variants/${variantId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function listLocationReferenceVersions(
  projectId: number,
  locationId: number,
  variantId: number | null | undefined,
  facing?: LocationStorableFacing,
): Promise<LocationReferenceVersionRow[]> {
  const params = new URLSearchParams()
  if (variantId === null) params.set("variant_id", "")
  else if (typeof variantId === "number") params.set("variant_id", String(variantId))
  if (facing) params.set("facing", facing)
  const qs = params.toString() ? `?${params.toString()}` : ""
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions${qs}`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  const data = await handleJson<{ versions: LocationReferenceVersionRow[] }>(res)
  return data.versions
}

export async function uploadLocationReferenceVersion(
  projectId: number,
  locationId: number,
  file: File,
  variantId: number | null,
  facing: LocationStorableFacing = PRIMARY_LOCATION_FACING,
) {
  const form = new FormData()
  form.append("file", file)
  if (variantId !== null) form.append("variant_id", String(variantId))
  form.append("facing", facing)

  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; version: LocationReferenceVersionRow }>(res)
}

export async function createLocationReferenceVersionFromSource(
  projectId: number,
  locationId: number,
  source: ShotFrameSource,
  variantId: number | null,
  facing: LocationStorableFacing = PRIMARY_LOCATION_FACING,
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      source,
      variant_id: variantId ?? undefined,
      facing,
    }),
  })
  return handleJson<{ ok: boolean; version: LocationReferenceVersionRow }>(res)
}

export async function clearLocationReferenceVersion(
  projectId: number,
  locationId: number,
  opts?: { facing?: LocationStorableFacing; variantId?: number | null },
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      clear: true,
      variant_id: opts?.variantId ?? undefined,
      facing: opts?.facing ?? PRIMARY_LOCATION_FACING,
    }),
  })
  return handleJson<{ ok: boolean; version: LocationReferenceVersionRow }>(res)
}

export async function activateLocationReferenceVersion(projectId: number, locationId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/locations/${locationId}/reference_versions/${versionId}/activate`,
    {
      method: "PATCH",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({}),
    },
  )
  return handleJson<{ ok: boolean; version: LocationReferenceVersionRow }>(res)
}

export async function deleteLocationReferenceVersion(projectId: number, locationId: number, versionId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions/${versionId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean }>(res)
}

export async function copyLocationFacing(
  projectId: number,
  locationId: number,
  from: LocationFacing,
  to: LocationFacing,
  variantId: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions/copy_facing`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      from,
      to,
      variant_id: variantId ?? undefined,
    }),
  })
  return handleJson<{ ok: boolean }>(res)
}

export async function swapLocationFacings(
  projectId: number,
  locationId: number,
  facingA: LocationFacing,
  facingB: LocationFacing,
  variantId: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/locations/${locationId}/reference_versions/swap_facings`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      facing_a: facingA,
      facing_b: facingB,
      variant_id: variantId ?? undefined,
    }),
  })
  return handleJson<{ ok: boolean }>(res)
}
